/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Opaque from "App/render/webgl/nodes/bins/opaque_bin";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import Pass from "App/render/webgl/passes/pass";
import WebGLContext from "App/web_gl_context";
import RenderBinNode from "App/render/webgl/nodes/render_bin_node";
import Factory from "App/render/webgl/nodes/bins/factory";
import {ParticlesBinTypeName} from "App/render/webgl/nodes/bins/type_names";
import IScene from "js_interface/src/scene_interface";

export default
class Particles extends Opaque {

    public static TypeName: string = ParticlesBinTypeName;

    private _lastTime: number;
    private _time: number;
    private _diff: number;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);

        this._lastTime = new Date().getTime();
        this._time = 0;
        this._diff = 0;
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): Particles {
        return new Particles(aSceneAPI, aRenderGraph);
    }

    public setUniforms(aRenderPass: Pass) {
        let view = aRenderPass.view;
        let shader: any = aRenderPass.getActiveShaderRenderable();
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        RenderBinNode.prototype.setUniforms.call(this, aRenderPass);

        let mvmatrix = view.getCamera().getViewMatrix();

        mvmatrix[12] = 0;
        mvmatrix[13] = 0;
        mvmatrix[14] = 0;
        mvmatrix[15] = 1;

        gl.uniformMatrix4fv(shader.uniformLocations.u_view_matrix_matrix, false, mvmatrix);
        gl.uniformMatrix4fv(shader.uniformLocations.u_projection_matrix, false, view.getCamera().getProjectionMatrixRef());

        gl.uniform1f(shader.uniformLocations.u_radiusX, 3.5);
        gl.uniform1f(shader.uniformLocations.u_radiusZ, 2.5);
        gl.uniform1f(shader.uniformLocations.u_size, 15.0);
        gl.uniform1f(shader.uniformLocations.u_scale, 4.0);
        gl.uniform1f(shader.uniformLocations.u_height, 10.0);

        gl.uniform1f(shader.uniformLocations.u_elapsedTime, this._time);
        gl.uniform1f(shader.uniformLocations.u_speedH, 0.8);
        gl.uniform1f(shader.uniformLocations.u_speedV, 1.8);
    }

    public tick() {
        if (this.isEmpty()) {
            return;
        }

        if (this._lastTime === 0) {
            this._diff = 0;
        } else {
            let currTime = new Date().getTime();
            this._diff = currTime - this._lastTime;
            if (this._diff > 33.3) {
                this._lastTime = currTime;
                this._sceneAPI.viewModeModule.scheduleRedraw();
                this._time += 0.0814;
            }
        }
    }

    public render(aRenderPass: Pass): boolean {
        if (this.isEmpty()) {
            return;
        }

        aRenderPass.bindShader(this._typeName);

        this.setUniforms(aRenderPass);

        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        // enable alpha blending
        gl.enable(gl.BLEND);
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE);

        RenderBinNode.prototype.render.call(this, aRenderPass);

        gl.disable(gl.BLEND);

        aRenderPass.unbindShader();
    }
}

Factory.register(Particles);
